import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import {
  Globe, ArrowRight, CheckCircle2, Clock, Sparkles, Zap, Layers,
  Ticket, QrCode, TrendingUp, RefreshCw, ClipboardCheck, Gauge,
  BadgeCheck, MapPin, Plug, ShieldCheck, Boxes, Search, Star,
} from 'lucide-react';

const H = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

/* ── scroll reveal ── */
const Reveal = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${shown ? 'reveal-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</Tag>;
};

/* ── status pill ── */
const STATUS = {
  available: { label: 'Available', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  soon: { label: 'Coming Soon', cls: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  request: { label: 'Available Upon Request', cls: 'text-sky-700 bg-sky-50 border-sky-200', dot: 'bg-sky-500' },
};
const StatusPill = ({ s }) => {
  const st = STATUS[s];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {st.label}
    </span>
  );
};

/* ── partner logo (image or initials fallback) ── */
const PartnerLogo = ({ p }) => (
  p.logo
    ? <img src={p.logo} alt={p.name} className="w-14 h-14 rounded-2xl object-cover border border-slate-100 shrink-0 bg-white" />
    : <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg shrink-0" style={{ backgroundColor: p.color }}>{p.initials}</div>
);

const GLOBAL_PARTNERS = [
  { name: 'Viator', logo: '/ota-logos/viator.jpeg', coverage: 'Worldwide · North America & Europe strength', status: 'available',
    desc: 'One of the largest global marketplaces for tours, activities and attraction tickets — ideal for reaching high-intent international travellers.' },
  { name: 'GetYourGuide', logo: '/ota-logos/getyourguide.png', coverage: 'Global · Strong in Europe & Western markets', status: 'available',
    desc: 'A leading experiences platform trusted by millions of travellers planning tours, day trips and attraction visits abroad.' },
  { name: 'Airbnb', logo: '/ota-logos/airbnb.jpeg', coverage: 'Global · Experiences audience', status: 'available',
    desc: 'Put your attraction in front of a worldwide community of travellers actively looking for things to do at their destination.' },
  { name: 'Google Things to Do', logo: '/ota-logos/google-things-to-do.png', coverage: 'Global · Google Search & Maps', status: 'available',
    desc: 'Surface your bookable tickets directly on Google Search and Maps — the moment travellers start planning their trip.' },
];

const ASIA_PARTNERS = [
  { name: 'Klook', logo: '/ota-logos/klook.png', coverage: 'Southeast & East Asia · APAC leader', status: 'available',
    desc: 'The go-to experiences app across Asia, connecting you to a vast base of regional and inbound travellers.' },
  { name: 'Trip.com', logo: '/ota-logos/tripcom.png', coverage: 'East Asia & Global · Massive APAC reach', status: 'available',
    desc: 'A major Asian travel platform giving your attraction exposure to millions of travellers across the region and beyond.' },
  { name: 'Traveloka', logo: '/ota-logos/traveloka.jpeg', coverage: 'Southeast Asia · Indonesia, Thailand, Malaysia', status: 'request',
    desc: 'A dominant Southeast Asian travel brand — a powerful way to capture demand from the fast-growing ASEAN market.' },
];

const REGIONAL_PARTNERS = [
  { name: 'KKday', initials: 'KK', color: '#ff5b2e', coverage: 'East & Southeast Asia', status: 'soon',
    desc: 'A popular Asian experiences marketplace expanding your reach with regional travellers seeking curated activities.' },
  { name: 'WAUG', initials: 'W', color: '#5b4bff', coverage: 'South Korea & inbound Asia', status: 'soon',
    desc: 'A leading Korean travel-experiences app, ideal for tapping into Korean outbound and inbound tourism demand.' },
  { name: 'Headout', initials: 'H', color: '#e8365a', coverage: 'APAC & global last-minute', status: 'soon',
    desc: 'A mobile-first platform focused on instant, last-minute bookings for attractions and experiences.' },
];

const BENEFITS = [
  { icon: Plug, t: 'One connection, many channels', d: 'Publish once on StayMii and distribute to a growing ecosystem of global and regional marketplaces.' },
  { icon: Globe, t: 'Expand internationally & across Asia', d: 'Reach travellers in new markets — with a strong emphasis on the fast-growing Asia region.' },
  { icon: TrendingUp, t: 'Reach millions of travellers', d: 'Tap into the audiences of the world’s most-visited travel and experience platforms.' },
  { icon: Boxes, t: 'Centralised ticket inventory', d: 'Manage all your ticket types, prices and capacity from a single source of truth.' },
  { icon: RefreshCw, t: 'Real-time pricing & availability', d: 'Update once and it syncs everywhere instantly — no oversells, no stale listings.' },
  { icon: QrCode, t: 'QR e-ticket validation', d: 'Every order issues a tamper-proof QR e-ticket that staff scan at the gate.' },
  { icon: Zap, t: 'Instant booking confirmation', d: 'Travellers get confirmed the moment they pay, across every connected channel.' },
  { icon: ClipboardCheck, t: 'Automated order management', d: 'Bookings from all marketplaces land in one dashboard — organised and ready.' },
  { icon: Gauge, t: 'Reduced operational workload', d: 'No manual feeds or spreadsheets. Automation handles the heavy lifting for you.' },
];

/* interactive coverage markers on a stylised dotted map */
const MARKERS = [
  { id: 'na', label: 'North America', x: 21, y: 34, region: 'Global' },
  { id: 'eu', label: 'Europe', x: 49, y: 30, region: 'Global' },
  { id: 'sea', label: 'Southeast Asia', x: 78, y: 56, region: 'Asia' },
  { id: 'ea', label: 'East Asia', x: 81, y: 39, region: 'Asia' },
  { id: 'sa', label: 'South Asia', x: 69, y: 47, region: 'Asia' },
  { id: 'oc', label: 'Oceania', x: 85, y: 70, region: 'Regional' },
];

const WorldMap = () => {
  const [active, setActive] = useState('sea');
  return (
    <div className="relative w-full rounded-3xl bg-[#0C2159] border border-cyan-400/20 p-6 sm:p-8 overflow-hidden shadow-[0_25px_60px_-20px_rgba(12,33,89,0.55)]" data-testid="gmc-worldmap">
      <div className="relative flex items-center justify-between mb-5">
        <p className="text-cyan-300 text-[11px] font-bold uppercase tracking-[0.2em]" style={MONO}>Global Coverage</p>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300" style={MONO}><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE NETWORK</span>
      </div>
      <div className="relative w-full" style={{ aspectRatio: '1264 / 848' }}>
        <img src="/world-map-dots.png" alt="StayMii global distribution coverage map" className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none" draggable="false" />
        {MARKERS.map((m) => (
          <button key={m.id} onMouseEnter={() => setActive(m.id)} onFocus={() => setActive(m.id)} onClick={() => setActive(m.id)}
            className="absolute -translate-x-1/2 -translate-y-1/2 group z-10" style={{ left: `${m.x}%`, top: `${m.y}%` }}
            data-testid={`gmc-marker-${m.id}`} aria-label={m.label}>
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400/40 animate-ping" style={{ width: 16, height: 16, animationDuration: '2.4s' }} />
            <span className={`relative block rounded-full ring-2 ring-[#0C2159] transition-all duration-300 ${active === m.id ? 'w-4 h-4 bg-cyan-300 shadow-[0_0_12px_2px_rgba(103,232,249,0.8)]' : 'w-3 h-3 bg-cyan-400'}`} />
            <span className={`absolute left-1/2 -translate-x-1/2 top-5 whitespace-nowrap text-[10px] font-bold px-2 py-0.5 rounded-md transition-opacity duration-200 ${active === m.id ? 'opacity-100 bg-white text-[#0C2159]' : 'opacity-0'}`} style={MONO}>{m.label}</span>
          </button>
        ))}
      </div>
      <div className="relative mt-4 grid grid-cols-3 gap-3">
        {[['Global', '4+'], ['Asia', '5+'], ['Regional', '3+']].map(([r, n]) => (
          <div key={r} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-center">
            <div className="text-cyan-300 font-black text-lg" style={MONO}>{n}</div>
            <div className="text-[9px] text-slate-400 tracking-widest mt-0.5" style={MONO}>{r.toUpperCase()} CHANNELS</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PartnerCard = ({ p, i }) => (
  <Reveal delay={(i % 3) * 60}>
    <div className="group h-full rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`gmc-partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <PartnerLogo p={p} />
        <StatusPill s={p.status} />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-1" style={H}>{p.name}</h3>
      <p className="text-[11px] font-semibold text-cyan-700 flex items-center gap-1.5 mb-2.5"><MapPin className="w-3.5 h-3.5" /> {p.coverage}</p>
      <p className="text-sm text-slate-500 leading-relaxed">{p.desc}</p>
    </div>
  </Reveal>
);

const RegionSection = ({ id, tag, title, subtitle, partners }) => (
  <section className="py-16 lg:py-20" data-testid={`gmc-region-${id}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl mb-10">
        <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-3" style={MONO}>{tag}</p>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2" style={H}>{title}</h2>
        <p className="text-slate-500 leading-relaxed">{subtitle}</p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {partners.map((p, i) => <PartnerCard key={p.name} p={p} i={i} />)}
      </div>
    </div>
  </section>
);

const GlobalMarketConnect = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="gmc-page">
      <SEO
        title="StayMii Global Market Connect — Attraction & Ticket Distribution"
        description="StayMii Global Market Connect helps attractions, museums, tours, theme parks and experience providers distribute tickets across leading global and Asian travel marketplaces from one platform — with real-time availability, QR e-tickets and instant confirmation."
        keywords="attraction ticket distribution, sell attraction tickets online, global market connect, tour distribution platform, asia travel marketplaces, klook traveloka trip.com distribution, experience distribution, museum ticketing distribution, staymii e-ticket"
        canonicalUrl="/global-market-connect"
      />
      <LandingNavBar />
      <StyleTag />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative overflow-hidden pt-28 pb-24 lg:pt-36 lg:pb-28 bg-gradient-to-b from-cyan-50 via-white to-white" data-testid="gmc-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[560px] h-[560px] bg-cyan-300/25 rounded-full blur-[150px]" />
          <div className="absolute -bottom-24 -left-20 w-[460px] h-[460px] bg-blue-300/20 rounded-full blur-[140px]" />
          <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: 'linear-gradient(#0f172a0d 1px,transparent 1px),linear-gradient(90deg,#0f172a0d 1px,transparent 1px)', backgroundSize: '48px 48px', maskImage: 'radial-gradient(ellipse at 50% 30%, black, transparent 75%)' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-cyan-200 shadow-sm mb-7 reveal reveal-in" data-testid="gmc-hero-badge">
              <Globe className="w-3.5 h-3.5 text-cyan-600" />
              <span className="text-cyan-700 text-[11px] font-bold uppercase tracking-[0.2em]" style={MONO}>StayMii · E-Ticket Distribution</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.98] text-slate-900 mb-4" style={H} data-testid="gmc-hero-title">
              StayMii Global<br />
              <span className="bg-gradient-to-r from-cyan-500 via-cyan-600 to-blue-600 bg-clip-text text-transparent">Market Connect</span>
            </h1>
            <p className="text-lg sm:text-xl font-bold text-slate-700 mb-6" style={H}>
              Reach More Travellers. Sell More Tickets. Connect Globally.
            </p>

            <p className="text-base text-slate-600 leading-relaxed max-w-xl mb-9 font-medium">
              StayMii lets attractions, museums, tours, activities, theme parks and experience providers grow their
              online presence by connecting to leading travel marketplaces and regional distribution partners —
              all through <span className="text-cyan-700 font-semibold">a single platform</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?subject=Request%20Integration%20—%20Global%20Market%20Connect" data-testid="gmc-hero-cta-request"
                className="cta-glow group inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl font-black text-sm tracking-wide transition-colors">
                Request Integration <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact?type=demo&subject=Book%20a%20Demo%20—%20Global%20Market%20Connect" data-testid="gmc-hero-cta-demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-xl font-semibold text-sm shadow-sm transition-colors">
                Book a Demo
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <WorldMap />
          </div>
        </div>
      </section>

      {/* ═══════════════ MARQUEE ═══════════════ */}
      <div className="border-y border-slate-100 bg-slate-50 py-5 overflow-hidden" data-testid="gmc-marquee">
        <div className="flex items-center gap-10 marquee whitespace-nowrap text-slate-400">
          {['VIATOR', 'GETYOURGUIDE', 'AIRBNB', 'GOOGLE THINGS TO DO', 'KLOOK', 'TRIP.COM', 'TRAVELOKA', 'GLOBAL REACH', 'ASIA MARKETS', 'VIATOR', 'GETYOURGUIDE', 'AIRBNB', 'GOOGLE THINGS TO DO', 'KLOOK', 'TRIP.COM', 'TRAVELOKA', 'GLOBAL REACH', 'ASIA MARKETS'].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.2em]" style={MONO}>
              <Zap className="w-3.5 h-3.5 text-cyan-500" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════ INTRO ═══════════════ */}
      <section className="pt-20 lg:pt-28 pb-4 bg-white" data-testid="gmc-network-intro">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>The Distribution Network</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4" style={H}>
              A world of travellers, connected through one platform
            </h2>
            <p className="text-slate-500 leading-relaxed">
              From global giants to specialist Asian channels, StayMii plugs your attraction into the marketplaces
              travellers already use — organised by the markets that matter most to your business.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ REGIONAL SECTIONS ═══════════════ */}
      <RegionSection id="global" tag="Global Marketplaces" title="Reach travellers worldwide"
        subtitle="The largest international platforms for tours, activities and attraction tickets — perfect for capturing inbound and outbound demand across the globe."
        partners={GLOBAL_PARTNERS} />

      <div className="bg-cyan-50/60 border-y border-cyan-100">
        <RegionSection id="asia" tag="Asia Marketplaces" title="Win the fast-growing Asia market"
          subtitle="The strongest channels for reaching travellers across Southeast and East Asia — where experience-led travel demand is booming."
          partners={ASIA_PARTNERS} />
      </div>

      <RegionSection id="regional" tag="Regional & Emerging Channels" title="Specialist reach, market by market"
        subtitle="Targeted regional platforms that unlock niche and high-value traveller segments as we expand the StayMii network."
        partners={REGIONAL_PARTNERS} />

      {/* ═══════════════ STATUS LEGEND ═══════════════ */}
      <section className="pb-16 bg-white" data-testid="gmc-legend">
        <div className="max-w-3xl mx-auto px-4 flex flex-wrap items-center justify-center gap-4">
          <StatusPill s="available" />
          <StatusPill s="soon" />
          <StatusPill s="request" />
        </div>
      </section>

      {/* ═══════════════ GOOGLE THINGS TO DO SPOTLIGHT ═══════════════ */}
      <section className="pb-20 lg:pb-24 bg-white" data-testid="gmc-google-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to="/google-things-to-do" data-testid="gmc-google-banner-link"
              className="gtd-banner group relative block overflow-hidden rounded-3xl bg-[#0C2159] px-7 py-10 sm:px-12 sm:py-12 shadow-[0_30px_70px_-25px_rgba(12,33,89,0.6)]">
              {/* animated google-colour glows */}
              <span className="pointer-events-none absolute -top-24 -left-16 w-80 h-80 rounded-full blur-[110px] opacity-60" style={{ background: '#4285F4' }} />
              <span className="pointer-events-none absolute -bottom-24 right-1/4 w-72 h-72 rounded-full blur-[110px] opacity-40" style={{ background: '#34A853' }} />
              <span className="pointer-events-none absolute top-1/3 -right-16 w-72 h-72 rounded-full blur-[110px] opacity-40" style={{ background: '#EA4335' }} />
              {/* sweeping shine */}
              <span className="gtd-shine pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              {/* google-colour top bar */}
              <span className="absolute top-0 left-0 right-0 h-1.5 flex">
                <span className="flex-1" style={{ background: '#4285F4' }} />
                <span className="flex-1" style={{ background: '#EA4335' }} />
                <span className="flex-1" style={{ background: '#FBBC05' }} />
                <span className="flex-1" style={{ background: '#34A853' }} />
              </span>

              <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur mb-5">
                    <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white" style={MONO}>Featured Channel · Now Live</span>
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4" style={H}>
                    Now selling on{' '}
                    <span className="whitespace-nowrap">
                      <span style={{ color: '#4285F4' }}>G</span><span style={{ color: '#EA4335' }}>o</span><span style={{ color: '#FBBC05' }}>o</span><span style={{ color: '#4285F4' }}>g</span><span style={{ color: '#34A853' }}>l</span><span style={{ color: '#EA4335' }}>e</span>
                    </span>{' '}
                    <span className="text-white">Things to Do</span>
                  </h2>
                  <p className="text-slate-300 leading-relaxed max-w-xl mb-7">
                    Put your attraction in front of travellers the instant they search on{' '}
                    <span className="text-white font-semibold">Google Search &amp; Maps</span>. Real-time availability,
                    instant confirmation and tamper-proof QR e-tickets — connected in one click.
                  </p>

                  <span className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-[#0C2159] font-black text-sm shadow-lg group-hover:gap-3 transition-all"
                    data-testid="gmc-google-banner-cta">
                    Explore Google Things to Do
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>

                  <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400 font-medium">
                    {['Google Search & Maps', 'Real-time sync', 'One-click connect'].map((t, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: '#34A853' }} />{t}</span>
                    ))}
                  </div>
                </div>

                {/* logo + search chip */}
                <div className="lg:col-span-4 flex justify-center lg:justify-end">
                  <div className="relative">
                    <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-3xl bg-white flex items-center justify-center shadow-2xl p-4 group-hover:scale-105 transition-transform duration-300">
                      <img src="/ota-logos/google-things-to-do.png" alt="Google Things to Do" className="w-full h-auto" />
                    </div>
                    <div className="absolute -bottom-4 -left-6 flex items-center gap-2 rounded-full bg-white shadow-xl px-3.5 py-2">
                      <Search className="w-4 h-4 text-slate-400" />
                      <span className="text-[11px] font-semibold text-slate-600" style={MONO}>things to do near me</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ BENEFITS ═══════════════ */}
      <section className="py-24 lg:py-28 bg-[#0C2159] relative overflow-hidden" data-testid="gmc-benefits">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'radial-gradient(rgba(103,232,249,0.9) 1px, transparent 1.4px)', backgroundSize: '22px 22px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-cyan-300 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>Why StayMii</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={H}>
              Everything you need to distribute at scale
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={(i % 3) * 60}>
                <div className="group h-full rounded-2xl border border-white/10 bg-white/5 p-6 hover:bg-white/10 transition-colors duration-300" data-testid={`gmc-benefit-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center mb-4">
                    <b.icon className="w-5 h-5 text-cyan-300" />
                  </div>
                  <h3 className="font-bold text-white mb-1.5" style={H}>{b.t}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-cyan-500 via-cyan-500 to-blue-600" data-testid="gmc-cta">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <Reveal className="relative max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-white items-center justify-center mb-6 shadow-xl"><Globe className="w-7 h-7 text-cyan-600" /></div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tighter mb-5" style={H}>Expand Your Distribution Network with StayMii</h2>
          <p className="text-white/90 leading-relaxed max-w-xl mx-auto mb-9">
            Whether you operate a museum, attraction, tour, cultural site or experience, StayMii helps you connect with
            leading travel marketplaces and regional distribution partners to maximise ticket sales and global visibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?subject=Request%20Integration%20—%20Global%20Market%20Connect" data-testid="gmc-cta-request"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-slate-50 text-cyan-700 rounded-xl font-black text-sm transition-colors shadow-lg">
              Request Integration <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact?type=demo&subject=Book%20a%20Demo%20—%20Global%20Market%20Connect" data-testid="gmc-cta-demo"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-600/40 hover:bg-cyan-600/60 text-white border border-white/40 rounded-xl font-semibold text-sm transition-colors">
              Book a Demo
            </Link>
            <Link to="/contact?subject=Contact%20Sales%20—%20Global%20Market%20Connect" data-testid="gmc-cta-sales"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-600/40 hover:bg-cyan-600/60 text-white border border-white/40 rounded-xl font-semibold text-sm transition-colors">
              Contact Sales
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/80 font-medium">
            {['One platform, many channels', 'Real-time availability', 'QR e-ticket validation', 'Built for Asia & beyond'].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-white" />{t}</span>
            ))}
          </div>
        </Reveal>
      </section>

      <LandingFooter />
    </div>
  );
};

const StyleTag = () => (
  <style>{`
    .reveal{opacity:0;transform:translateY(26px);transition:opacity .7s cubic-bezier(.2,.7,.2,1),transform .7s cubic-bezier(.2,.7,.2,1)}
    .reveal-in{opacity:1;transform:none}
    .cta-glow{animation:ctaPulse 2.8s ease-in-out infinite}
    @keyframes ctaPulse{0%,100%{box-shadow:0 0 0 0 rgba(6,182,212,.35)}50%{box-shadow:0 0 26px 3px rgba(6,182,212,.45)}}
    .gtd-shine{animation:gtdShine 4.5s ease-in-out infinite}
    @keyframes gtdShine{0%{left:-33%}55%,100%{left:130%}}
    .marquee{animation:marq 34s linear infinite;width:max-content}
    @keyframes marq{from{transform:translateX(0)}to{transform:translateX(-50%)}}
    @media (prefers-reduced-motion: reduce){
      .reveal{opacity:1;transform:none;transition:none}
      .cta-glow,.marquee,.gtd-shine{animation:none}
    }
  `}</style>
);

export default GlobalMarketConnect;
